import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Plus, Search, Trash2 } from 'lucide-react';
import { get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, DateTimePicker, Field, Input, Textarea } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';

export type InvestigationKind = 'lab' | 'radiology';
export type OrderPriority = 'routine' | 'urgent' | 'stat';

interface CatalogueRow {
  id: string;
  code: string;
  name: string;
  kind: 'test' | 'panel' | 'study';
  detail: string;
}

export interface InvestigationOrderPanelProps {
  patientId: string;
  encounterId?: string | null;
  admissionId?: string | null;
  erCaseId?: string | null;
  defaultPriority?: OrderPriority;
  readOnly?: boolean;
  initialOutsideLines?: string[];
  initialKind?: InvestigationKind;
  initialQuery?: string;
  title?: string;
  onSaved: () => void;
}

type Paged<T> = { data?: T[] } | T[];

const IONISING_MODALITIES = ['xray', 'ct', 'mammography', 'fluoroscopy'];

export function mayBePregnant(patient: { sex: string; dob: string | null; approximateAgeYears: number | null }): boolean {
  if (patient.sex !== 'female') return false;
  const years = patient.dob ? (Date.now() - new Date(patient.dob).getTime()) / (365.25 * 86_400_000) : patient.approximateAgeYears;
  if (years === null || years === undefined) return true;
  return years >= 12 && years <= 55;
}

function rowsOf<T>(value: Paged<T>): T[] {
  return Array.isArray(value) ? value : value.data ?? [];
}

export function investigationKindsFor(hasModule: (module: InvestigationKind) => boolean): { kind: InvestigationKind; licensed: boolean }[] {
  return (['lab', 'radiology'] as const).map((kind) => ({ kind, licensed: hasModule(kind) }));
}

export function outsideLinesFrom(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, 50);
}

export function InvestigationOrderPanel({
  patientId,
  encounterId,
  admissionId,
  erCaseId,
  defaultPriority = 'routine',
  readOnly = false,
  initialOutsideLines,
  initialKind,
  initialQuery,
  title = 'Order investigations',
  onSaved,
}: InvestigationOrderPanelProps) {
  const toast = useToast();
  const { hasModule } = useAuth();
  const kinds = investigationKindsFor((module) => hasModule(module));
  const firstLicensed = kinds.find((entry) => entry.licensed)?.kind ?? 'lab';
  const [kind, setKind] = useState<InvestigationKind>(initialKind ?? firstLicensed);
  const licensed = kinds.find((entry) => entry.kind === kind)?.licensed ?? false;
  const [outsideMode, setOutsideMode] = useState(Boolean(initialOutsideLines?.length) || !licensed);
  const outside = outsideMode || !licensed;
  const [query, setQuery] = useState(initialQuery ?? '');
  const [chosen, setChosen] = useState<CatalogueRow[]>([]);
  const [outsideText, setOutsideText] = useState((initialOutsideLines ?? []).join('\n'));
  const [priority, setPriority] = useState<OrderPriority>(defaultPriority);
  const [collectAt, setCollectAt] = useState('');
  const [pregnancyAnswer, setPregnancyAnswer] = useState<'' | 'not_pregnant' | 'pregnant'>('');
  const [pregnancyJustification, setPregnancyJustification] = useState('');
  const pregnancyChecked = pregnancyAnswer !== '';
  const patient = useQuery({
    queryKey: ['patient-basics', patientId],
    queryFn: () => get<{ sex: string; dob: string | null; approximateAgeYears: number | null }>(`/patients/${patientId}`),
    enabled: kind === 'radiology' && !readOnly,
    staleTime: 300_000,
  });
  const ionisingChosen = chosen.some((row) => IONISING_MODALITIES.includes(row.detail.toLowerCase()));
  const askPregnancy = kind === 'radiology' && !outside && ionisingChosen && patient.data ? mayBePregnant(patient.data) : false;
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const catalogue = useQuery({
    queryKey: ['order-catalogue', kind, query],
    queryFn: async (): Promise<CatalogueRow[]> => {
      if (kind === 'lab') {
        const [tests, panels] = await Promise.all([
          get<Paged<{ id: string; code: string; name: string; specimenType: string }>>(`/lab/tests${qs({ q: query || undefined, limit: 20 })}`),
          get<Paged<{ id: string; code: string; name: string }>>(`/lab/panels${qs({ q: query || undefined, limit: 10 })}`),
        ]);
        return [
          ...rowsOf(panels).map((p) => ({ id: p.id, code: p.code, name: p.name, kind: 'panel' as const, detail: 'Panel' })),
          ...rowsOf(tests).map((t) => ({ id: t.id, code: t.code, name: t.name, kind: 'test' as const, detail: t.specimenType })),
        ];
      }
      const studies = await get<Paged<{ id: string; code: string; name: string; modality: string }>>(`/radiology/study-types${qs({ q: query || undefined, limit: 20 })}`);
      return rowsOf(studies).map((s) => ({ id: s.id, code: s.code, name: s.name, kind: 'study' as const, detail: s.modality.toUpperCase() }));
    },
    enabled: !readOnly && !outside,
  });

  const lines = outsideLinesFrom(outsideText);
  const ready = outside ? lines.length > 0 : chosen.length > 0;

  const order = useMutation({
    mutationFn: () =>
      post('/clinical-orders', {
        patientId,
        encounterId: encounterId ?? undefined,
        admissionId: admissionId ?? undefined,
        erCaseId: erCaseId ?? undefined,
        type: kind,
        priority,
        outside,
        clinicalNotes: notes || undefined,
        collectAt: kind === 'lab' && collectAt && !outside ? new Date(collectAt).toISOString() : undefined,
        pregnancyChecked: kind === 'radiology' ? pregnancyChecked : undefined,
        ...(kind === 'radiology' && pregnancyAnswer === 'pregnant' ? { pregnant: true, pregnancyJustification: pregnancyJustification.trim() } : {}),
        items: outside ? lines.map((label) => ({ label })) : chosen.map((c) => ({ refId: c.id, label: c.name })),
      }),
    onSuccess: () => {
      toast.success(
        outside
          ? tr('opd', 'written_as_an_outside_requisition_it_prints')
          : kind === 'lab'
            ? tr('opd', 'tests_ordered_the_lab_has_them_on_its_worklist')
            : tr('opd', 'imaging_ordered_radiology_has_it_on_the_worklist'),
      );
      setChosen([]);
      setPregnancyAnswer('');
      setPregnancyJustification('');
      setOutsideText('');
      setNotes('');
      setCollectAt('');
      onSaved();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  if (readOnly) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {error && <Alert variant="destructive">{error}</Alert>}
        <div className="flex flex-wrap gap-2">
          <ChoiceButtons<InvestigationKind>
            label={tr('opd', 'kind_of_investigation')}
            value={kind}
            onChange={(next) => {
              setKind(next);
              setChosen([]);
              setOutsideMode(!(kinds.find((entry) => entry.kind === next)?.licensed ?? false));
            }}
            size="sm"
            inline
            options={kinds.map((entry) => {
              const name = entry.kind === 'lab' ? tr('opd', 'laboratory_2') : tr('opd', 'imaging_2');
              return { value: entry.kind, label: entry.licensed ? name : `${name} · ${tr('opd', 'order_outside')}` };
            })}
          />
          <ChoiceButtons<OrderPriority>
            label={tr('opd', 'priority')}
            value={priority}
            onChange={setPriority}
            size="sm"
            inline
            options={[
              { value: 'routine', label: tr('opd', 'routine') },
              { value: 'urgent', label: tr('opd', 'urgent') },
              { value: 'stat', label: tr('opd', 'stat') },
            ]}
          />
        </div>

        {!licensed && (
          <Alert>
            {tr('opd', 'this_hospital_does_not_run')} {kind === 'lab' ? tr('opd', 'a_laboratory') : tr('opd', 'imaging_3')} {tr('opd', 'here_write_what_the_patient_should_have_done')}
          </Alert>
        )}

        {outside ? (
          <Field label={tr('opd', 'outside_investigations')} hint={tr('opd', 'one_per_line')}>
            <Textarea rows={3} value={outsideText} onChange={(e) => setOutsideText(e.target.value)} placeholder={kind === 'lab' ? tr('opd', 'serum_ferritin_vitamin_d') : tr('opd', 'mri_knee_right')} />
          </Field>
        ) : (
          <Field label={kind === 'lab' ? tr('opd', 'tests_and_panels') : tr('opd', 'studies')} hint={tr('opd', 'search_then_click_to_add')}>
            <div className="relative">
              <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input className="ps-9" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={kind === 'lab' ? tr('opd', 'cbc_hba1c_lipid_profile') : tr('opd', 'chest_x_ray_ultrasound_abdomen')} />
            </div>
            <div className="mt-2 max-h-40 overflow-y-auto rounded-md border">
              {(catalogue.data ?? [])
                .filter((r) => !chosen.some((c) => c.id === r.id))
                .slice(0, 12)
                .map((row) => (
                  <button
                    key={row.id}
                    type="button"
                    className="flex w-full items-center justify-between px-3 py-1.5 text-start text-sm hover:bg-accent"
                    onClick={() => setChosen((current) => [...current, row])}
                  >
                    <span>
                      {row.name} <span className="font-mono text-xs text-muted-foreground">{row.code}</span>
                    </span>
                    <span className="text-xs text-muted-foreground">{row.detail}</span>
                  </button>
                ))}
              {catalogue.data && catalogue.data.length === 0 && <p className="px-3 py-2 text-sm text-muted-foreground">{tr('opd', 'nothing_matches')}</p>}
            </div>
          </Field>
        )}

        {licensed && (
          <button type="button" className="text-xs text-primary underline-offset-2 hover:underline" onClick={() => setOutsideMode(!outsideMode)}>
            {outside ? tr('opd', 'back_to_our_catalogue') : tr('opd', 'not_in_our_catalogue_write_as_outside_test')}
          </button>
        )}

        {!outside && chosen.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {chosen.map((c) => (
              <Badge key={c.id} variant="secondary" className="gap-1">
                {c.name}
                <button type="button" aria-label={tr('opd', 'remove_x', { name: c.name })} onClick={() => setChosen((current) => current.filter((x) => x.id !== c.id))}>
                  <Trash2 className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}

        {!outside && kind === 'lab' && (
          <Field label={tr('opd', 'collect_at')} hint={tr('opd', 'leave_empty_to_collect_now_e_g_fasting_sugar')}>
            <DateTimePicker value={collectAt} onChange={(value) => setCollectAt(value)} />
          </Field>
        )}
        {askPregnancy && (
          <div className="space-y-2 rounded-md border border-warning/40 p-3 text-sm">
            <p className="font-medium">{tr('opd', 'could_the_patient_be_pregnant_x_ray_ct_and')}</p>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant={pregnancyAnswer === 'not_pregnant' ? 'default' : 'outline'} onClick={() => setPregnancyAnswer('not_pregnant')}>
                {tr('opd', 'not_pregnant')}
              </Button>
              <Button size="sm" variant={pregnancyAnswer === 'pregnant' ? 'destructive' : 'outline'} onClick={() => setPregnancyAnswer('pregnant')}>
                {tr('opd', 'pregnant_proceed')}
              </Button>
            </div>
            {pregnancyAnswer === 'pregnant' && (
              <Field label={tr('opd', 'why_the_study_goes_ahead')} required>
                <Input value={pregnancyJustification} maxLength={600} onChange={(e) => setPregnancyJustification(e.target.value)} />
              </Field>
            )}
          </div>
        )}
        <Field label={outside ? tr('opd', 'notes') : tr('opd', 'clinical_notes_for_the_lab_radiologist')}>
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        <Button
          onClick={() => {
            setError(null);
            order.mutate();
          }}
          loading={order.isPending}
          disabled={!ready || (askPregnancy && (!pregnancyAnswer || (pregnancyAnswer === 'pregnant' && pregnancyJustification.trim().length < 5)))}
        >
          <Plus className="me-1 h-4 w-4" />
          {outside ? tr('opd', 'write_outside_requisition') : `Order ${chosen.length || ''} ${kind === 'lab' ? 'test(s)' : 'study(ies)'}`}
        </Button>
      </CardContent>
    </Card>
  );
}
